<script setup>
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';
import { cloneDeep, orderBy, uniq } from 'lodash-es';
import { reactive } from 'vue';
import { useToast } from 'vue-toastification';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { csvInjectionProtector } from '~/common/utils/common.utils.js';
import { useTerraStore } from '~/terra/store/terra.store.js';

const emits = defineEmits(['close']);

const {
  $services,
  $t,
  $toast,
  auth_store,
  common_store,
  route,
} = useCommonImports();

dayjs.extend(utc);
dayjs.extend(timezone);
const toastification = useToast();
const terra_store = useTerraStore();

const form = ref({
  file: null,
});

const state = reactive({
  progress_error_toast: null,
  progress_updates: [],
  features_map: {},
  import_summary: {
    show: false,
    total: 0,
    success: 0,
    failed: 0,
  },
  report_data: [],
});

const get_timezone = computed(() => auth_store.logged_in_user_details?.timezone || dayjs.tz.guess());

// Same scopes the progress form uses for its member and category pickers
const users_by_email = computed(() => common_store.scope_users(route.params.asset_id).reduce((acc, user) => {
  if (user.email)
    acc[user.email.trim().toLowerCase()] = user.uid;
  return acc;
}, {}));
const categories_by_name = computed(() => common_store.scope_categories(route.params.asset_id).reduce((acc, category) => {
  if (category.name)
    acc[category.name.trim().toLowerCase()] = category.uid;
  return acc;
}, {}));

function convertToUTCWithUserTime(date) {
  const current_time = dayjs();
  const local_date = dayjs.tz(date, get_timezone.value);

  const is_today = local_date.format('YYYY-MM-DD') === current_time.format('YYYY-MM-DD');

  const local_date_with_time = local_date
    .hour(is_today ? current_time.hour() : 18)
    .minute(is_today ? current_time.minute() : 0)
    .second(is_today ? current_time.second() : 0)
    .millisecond(is_today ? current_time.millisecond() : 0);

  return local_date_with_time.utc().toISOString();
}
function getWorkflowType(workflow) {
  return workflow.data[Object.keys(workflow.data)[0]]?.progress_type || 'units';
}

async function downloadTemplate() {
  const Papa = await import('papaparse');
  const { saveAs } = await import('file-saver');

  const fields = [
    'Vector ID',
    'Vector Name',
    'Activity ID',
    'Activity',
    'Sub Activity ID',
    'Sub Activity',
    'Current',
    'Scope',
    'Date',
    'Value',
    'User',
    'Category',
  ];

  const features_with_workflow = terra_store.features_on_map.reduce((acc, f) => {
    const workflow_id = f.properties.workflow;
    const workflow = terra_store.terra_workflows[workflow_id];
    if (workflow) {
      const progress = f.properties.workflowProgress;
      const vector_details = {
        'Vector ID': f.properties.uid,
        'Vector Name': f.properties.name,
        'Activity ID': workflow_id,
        'Activity': workflow.name,
        'Date': '',
        'Value': '',
        'User': '',
        'Category': '',
      };
      const workflow_type = getWorkflowType(workflow);
      if (workflow_type === 'units') {
        const workflow_data = terra_store.workflow_fields_hash({ workflow: workflow_id });
        const sub_activities = orderBy(Object.values(workflow_data?.fields || {}), 'field_index').reduce((acc, field) => {
          acc.push({
            ...vector_details,
            'Sub Activity ID': field.uid,
            'Sub Activity': field.name,
            'Current': progress[field.uid]?.current,
            'Scope': progress[field.uid]?.total,
          });
          return acc;
        }, []);
        acc.push(...sub_activities);
      }
      else {
        const stage = Object.values(workflow.data)[0];
        if (stage) {
          acc.push(...stage.fields.map(field => ({
            ...vector_details,
            'Sub Activity ID': field.uid,
            'Sub Activity': field.name,
            'Current': progress[field.uid]?.current,
            'Scope': progress[field.uid]?.total,
          })));
        }
      }
    }
    return acc;
  }, []);

  const csv = Papa.unparse({
    fields,
    data: features_with_workflow,
  });
  const file_blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const file = new File([file_blob], 'sample_template.csv');
  saveAs(file);
}
async function downloadReport() {
  const Papa = await import('papaparse');
  const { saveAs } = await import('file-saver');

  const export_fields = [
    'Vector Name',
    'Vector ID',
    'Activity',
    'Activity ID',
    'Sub Activity',
    'Sub Activity ID',
    'Date',
    'Value',
    'User',
    'Category',
    'Notes',
    'Status',
    'Errors',
  ];

  // Transform data to include only specified fields and format errors
  const export_data = state.report_data.map((row) => {
    const formatted_row = {};
    export_fields.forEach((field) => {
      if (field === 'Errors') {
        formatted_row[field] = (uniq(row.__errors || [])).join(', ');
      }
      else if (field === 'Status') {
        formatted_row[field] = row.__status === 'success' ? 'Success' : 'Failed';
      }
      else if (field === 'Date') {
        formatted_row[field] = row.progress_timestamp;
      }
      else {
        formatted_row[field] = row[field] || '';
      }
    });
    return formatted_row;
  });

  const csv = Papa.unparse(export_data, {
    fields: export_fields,
  });

  const file_blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const file = new File([file_blob], 'Progress Import Report.csv');
  saveAs(file);
}
async function parseCSV(file) {
  const Papa = await import('papaparse');
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transform: (value) => {
        // Apply CSV injection protection to all values
        return csvInjectionProtector(value, ['=', '+', '@']);
      },
      transformHeader: h => (typeof h === 'string' ? h.trim() : h),
      complete: results => resolve(results.data || []),
      error: err => reject(err),
    });
  });
}

async function onImportFile() {
  state.progress_updates = [];
  state.import_summary = { total: 0, success: 0, failed: 0, show: false };

  if (state.progress_error_toast) {
    toastification.dismiss(state.progress_error_toast);
    state.progress_error_toast = null;
  }

  const file_data = form.value.file;
  if (!file_data)
    return;
  const filename = file_data?.name || '';
  const filename_split = filename.trim().split('.');
  const file_extension = filename_split.pop();
  if (filename_split.length === 0 || (filename_split.length > 1 && !['csv'].includes(file_extension))) {
    return;
  }

  let data = [];
  if (file_extension === 'csv') {
    data = await parseCSV(file_data);
  }

  state.progress_updates = data.map((row, index) => ({ ...row, unique_id: (index + 1) })).filter((row) => {
    return row['Vector ID'] && row['Sub Activity ID'] && row.Date && row.Value;
  });
}
function processImportedData(rows) {
  const last_occurrence = new Map();
  const today = dayjs().endOf('day');

  const sorted_rows = rows.sort((a, b) => {
    return new Date(a.Date) - new Date(b.Date);
  });

  const data = sorted_rows.map((row) => {
    const index = row.unique_id;
    const formatted_row = row;
    formatted_row.__errors = [];
    const feature = cloneDeep(terra_store.filtered_features_hash[formatted_row['Vector ID']]);

    if (row.Date) {
      const last_progress_timestamp = feature?.properties?.workflowProgressTimestamp;

      if (last_progress_timestamp) {
        const import_date = dayjs(row.Date).startOf('day');
        const last_update = dayjs(last_progress_timestamp).startOf('day');
        if (import_date.isBefore(last_update)) {
          throw new Error(`Date at row ${index + 1} is before the last progress update`);
        }
      }
      const date = dayjs(row.Date);
      if (date.isValid()) {
        if (date.isAfter(today)) {
          throw new Error(`Invalid date at row ${index + 1}. Future updates are ignored`);
        }
        formatted_row.Date = row.Date;
        formatted_row.formatted_date = date.format('YYYY-MM-DD');
      }
      else {
        throw new Error(`Invalid date at row ${index + 1}`);
      }
    }

    if (row.Value) {
      const raw_value = (row.Value === undefined || row.Value === null) ? '' : String(row.Value).trim();
      if (raw_value === '' || Number.isNaN(Number(raw_value))) {
        throw new Error(`Invalid value at row ${index + 1}`);
      }
      else {
        const number_value = Number(raw_value);
        if (number_value < 0) {
          throw new Error(`Invalid value at row ${index + 1}. Value cannot be negative`);
        }
        if (!Number.isInteger(number_value)) {
          throw new TypeError(`Invalid value at row ${index + 1}. Value cannot be a decimal`);
        }
        formatted_row.Value = number_value;
      }
    }

    const feature_type = feature?.properties?.featureType;
    if (!feature) {
      throw new Error(`Vector ID at row ${index + 1} not present on the map`);
    }
    else {
      formatted_row['Vector Name'] = feature.properties.name;
      const activity = terra_store.terra_workflows[feature.properties.workflow];
      if (!activity) {
        if (feature.properties.workflow) {
          throw new Error(`Activity not found for vector at row ${index + 1}`);
        }
        else {
          throw new Error(`No activity found for vector at row ${index + 1}`);
        }
      }
      else {
        if (!feature_type) {
          throw new Error(`No class found for vector at row ${index + 1}`);
        }
        else if (!activity.data[feature_type]) {
          throw new Error(`No activity stage found for the vector class at row ${index + 1}`);
        }
      }
    }
    if (row['Sub Activity ID'] && feature && feature_type) {
      const activity = terra_store.terra_workflows[feature.properties.workflow];
      const field = terra_store.all_workflow_fields_hash[row['Sub Activity ID']];
      if (!field) {
        throw new Error(`Sub Activity ID not found in the Activity at row ${index + 1}`);
      }
      else {
        if (activity) {
          formatted_row.Activity = activity?.name;
          formatted_row['Activity ID'] = activity?.uid;
        }
        if (field) {
          formatted_row['Sub Activity ID'] = field?.uid;
          formatted_row['Sub Activity'] = field?.name;
        }
      }
    }

    if (row.User) {
      const email = String(row.User).trim().toLowerCase();
      const user_uid = users_by_email.value[email];
      if (!user_uid) {
        throw new Error(`User ${row.User} at row ${index + 1} does not have access to this asset`);
      }
      formatted_row.user_uid = user_uid;
    }
    else {
      formatted_row.user_uid = auth_store.logged_in_user_id;
    }

    // A missing or unknown category is ignored rather than failing the row
    const category_name = String(row.Category || '').trim().toLowerCase();
    formatted_row.category_uid = categories_by_name.value[category_name] || null;

    formatted_row.signature = [
      formatted_row['Vector ID'] || '',
      formatted_row['Sub Activity ID'] || '',
      row.Date || '',
      row.Current || '',
      row.Value || '',
      row.User || '',
      row.Category || '',
      row.Notes || '',
    ].join('|');

    last_occurrence.set(formatted_row.signature, index);

    return formatted_row;
  });

  data.forEach((row) => {
    const last_index = last_occurrence.get(row.signature);
    if (last_index !== row.unique_id) {
      row.__errors.push('Duplicate row');
    }
  });

  const date_wise_feature_updates = data.reduce((acc, row) => {
    if (row.__errors.length > 0)
      return acc;
    if (!acc[row['Vector ID']])
      acc[row['Vector ID']] = {};

    if (!acc[row['Vector ID']][row.formatted_date])
      acc[row['Vector ID']][row.formatted_date] = [];

    acc[row['Vector ID']][row.formatted_date].push(row);
    return acc;
  }, {});

  return date_wise_feature_updates;
}

function getUpdatedValues(feature, row) {
  const progress = feature.properties.workflowProgress;
  const sub_activity = row['Sub Activity ID'];

  const feature_subactivity_progress = progress[sub_activity];

  if (!feature_subactivity_progress) {
    throw new Error(`Sub Activity ${row['Sub Activity']} is not associated with the vector ${row['Vector Name']} at row ${row.unique_id}`);
  }

  const active = feature_subactivity_progress.active;

  if (!active) {
    throw new Error(`Sub Activity ${row['Sub Activity']} is not active for the vector ${row['Vector Name']} at row ${row.unique_id}`);
  }

  progress[sub_activity].current += row.Value;
  if (progress[sub_activity].total < progress[sub_activity].current) {
    throw new Error(`Sub activity ${row['Sub Activity']} current value is greater than the scope for the vector ${row['Vector Name']} at row ${row.unique_id}`);
  }

  return {
    is_successful: true,
    current: progress[sub_activity].current,
    value: row.Value,
  };
}
function getDestinationForCumulative(progress, workflow_stage) {
  const new_cumulative_progress = workflow_stage.fields.reduce((acc, field) => {
    const weight = workflow_stage.weights[field.uid] ?? 1;

    const current_value = progress[field.uid]?.current || 0;
    const total = progress[field.uid]?.total;
    const current_progress = current_value / total;
    return acc + current_progress * weight;
  }, 0);
  let new_feature_type;
  for (const [feature_type, [min, max]] of Object.entries(
    workflow_stage.destination,
  )) {
    if (min <= new_cumulative_progress && new_cumulative_progress <= max)
      new_feature_type = feature_type;
  }
  return new_feature_type;
}
function getStatusChange(feature, workflow_stage) {
  let status_changed = false;
  const old_feature_type = feature.properties.featureType;
  let new_feature_type = null;
  const progress = feature.properties.workflowProgress;

  const workflow_type = workflow_stage.progress_type || 'units';
  if (workflow_type === 'cumulative') {
    new_feature_type = getDestinationForCumulative(progress, workflow_stage);
    if (new_feature_type !== old_feature_type) {
      status_changed = true;
    }
  }
  else {
    let required_total = 0;
    let actual_total = 0;

    const required_fields = workflow_stage.required || {};

    if (Object.keys(required_fields).length === 0) {
      throw new Error(`Required fields not found for the workflow stage for vector ${row['Vector Name']}`);
    }

    let all_required_fields_present = true;
    for (const field_uid in required_fields) {
      const req_percentage = required_fields[field_uid];
      required_total = req_percentage * (progress[field_uid]?.total || 0);
      actual_total = (progress[field_uid]?.current || 0);
      if (required_total > actual_total) {
        all_required_fields_present = false;
        break;
      }
    }

    if (all_required_fields_present) {
      new_feature_type = workflow_stage.destination;
      status_changed = true;
    }

    if (old_feature_type === new_feature_type) {
      status_changed = false;
    }
  }

  return {
    status_changed,
    old_feature_type,
    new_feature_type,
    workflow_type,
  };
}

async function handleProgressUpdate(payload) {
  const response = await $services.features.update_workflow_progress({
    container_id: terra_store.container.uid,
    body: payload,
  });
  let features = response?.data?.features || [];
  features = features.map(feature => ({
    ...feature,
    properties: {
      ...feature.properties,
      oldfeatureTypeId: terra_store.feature_types_by_uid[
        terra_store.selected_features[0]?.properties?.featureType
      ]?.id,
    },
  }));
  terra_store.selected_features = features;
  await terra_store.create_or_update_selected_features({
    reselectFeatures: true,
    updateFeatureRequest: false,
    properties_to_update: ['workflowProgress', 'workflow', 'featureTypeId', 'featureType', 'workflowProgressTimestamp'],
  });
  terra_store.selected_features = [];
}
async function updateProgress(date_wise_feature_updates, iteration = 0, skipAPI = true) {
  const payload = {};
  const status = skipAPI ? 'validated' : 'success';

  for (const feature_id in date_wise_feature_updates) {
    const feature = cloneDeep(state.features_map[feature_id]);
    if (!feature)
      throw new Error(`Vector not found for id ${feature_id}`);

    const workflow = terra_store.terra_workflows[feature?.properties?.workflow];
    if (!workflow)
      throw new Error(`Activity not found for vector ${feature_id}`);

    let workflow_stage = workflow.data[feature.properties.featureType];
    const feature_type_name = terra_store.feature_types_by_uid[feature.properties.featureType]?.name;
    if (!workflow_stage)
      throw new Error(`Activity stage not found for vector ${feature_id} for the class ${feature_type_name}`);

    const dates = Object.keys(date_wise_feature_updates[feature_id]);
    dates.sort((a, b) => new Date(a) - new Date(b));

    let progress_updates = [];
    let selected_date = null;
    for (const date of dates) {
      const rows = date_wise_feature_updates[feature_id][date];

      const pending_rows = rows.filter(row => row.__status !== status);

      if (pending_rows.length) {
        selected_date = date;
        // An update carries a single user and category, so batch only the rows sharing them.
        // The remaining rows are picked up by the next iteration.
        const { user_uid, category_uid } = pending_rows[0];
        progress_updates = pending_rows.filter(row => row.user_uid === user_uid && row.category_uid === category_uid);
        break;
      }
    }

    if (progress_updates.length === 0)
      continue;

    const update_object = {
      old_feature_type: feature.properties.featureType,
      new_feature_type: null,
      status_changed: true,
      notes: '',
      category: progress_updates[0].category_uid || null,
      user: progress_updates[0].user_uid || undefined,
      attachments: [],
      timestamp: convertToUTCWithUserTime(selected_date),
      progress: {},
    };

    for (let i = 0; i < progress_updates.length; i++) {
      const row = progress_updates[i];
      const sub_activity = row['Sub Activity ID'];
      if (workflow_stage.fields.find(field => field.uid === sub_activity) === undefined) {
        progress_updates[i].__status = 'failed';
        progress_updates[i].__errors.push('Sub Activity not found in the current active step of the activity');
        continue;
      }
      const value_status = getUpdatedValues(feature, row);
      update_object.progress[row['Sub Activity ID']] = {
        current: value_status.current,
        value: (update_object.progress[row['Sub Activity ID']]?.value || 0) + value_status.value,
      };
      progress_updates[i].__errors = [];
      progress_updates[i].__status = status;
    }

    let started = false;
    let new_feature_type = null;
    let status_changed = false;
    let status_updates = {};

    while (!started || new_feature_type !== null) {
      started = true;

      if (!workflow_stage) {
        break;
      }

      status_updates = getStatusChange(feature, workflow_stage);
      if (status_updates?.status_changed) {
        status_changed = true;
        new_feature_type = status_updates.new_feature_type;
        feature.feature_type = new_feature_type;

        if (status_updates.workflow_type === 'cumulative') {
          break;
        }

        // Update workflow_stage to next stage for new feature type
        workflow_stage = (workflow.data && workflow.data[new_feature_type]) || null;
      }
      else {
        break;
      }
    }

    if (status_updates) {
      if (workflow.data[new_feature_type]) {
        feature.properties.featureType = new_feature_type;
        feature.properties.workflowProgressTimestamp = update_object.timestamp;
      }
      status_updates.status_changed = status_changed;
      status_updates.new_feature_type = new_feature_type;
    }
    // Apply status updates
    Object.assign(update_object, (status_updates || {}));

    if (Object.keys(update_object.progress).length > 0) {
      payload[feature_id] = update_object;
    }

    date_wise_feature_updates[feature_id][selected_date]
      = date_wise_feature_updates[feature_id][selected_date].map((row) => {
        const updated_row = progress_updates.find(pu => pu.unique_id === row.unique_id);
        if (updated_row) {
          updated_row.progress_timestamp = dayjs(update_object.timestamp).format('YYYY-MM-DD hh:mm a');
          return updated_row;
        }
        return row;
      });

    state.features_map[feature_id] = feature;
  }

  if (!skipAPI && Object.keys(payload).length > 0) {
    try {
      await handleProgressUpdate(payload);
    }
    catch (error) {
      date_wise_feature_updates[feature_id][selected_date] = progress_updates.map((row) => {
        row.__errors = [(error.message || 'Failed to update progress')];
        return row;
      });
      logger.log(error);
    }
  }
  if (Object.keys(payload).length === 0) {
    return;
  }
  iteration += 1;
  return (await updateProgress(date_wise_feature_updates, iteration, skipAPI));
}
async function executeProgressUpdate() {
  const date_wise_feature_updates = processImportedData(state.progress_updates);

  state.features_map = cloneDeep({ ...terra_store.filtered_features_hash });
  // dry run to check if there are some errors
  await updateProgress(date_wise_feature_updates, 0, true);

  state.features_map = cloneDeep({ ...terra_store.filtered_features_hash });
  // actual run to update progress
  await updateProgress(date_wise_feature_updates, 0, false);

  state.features_map = {};
  terra_store.update_map_features_and_polygon();

  return date_wise_feature_updates;
}

async function onSave() {
  try {
    const date_wise_feature_updates = await executeProgressUpdate();

    state.report_data = [];
    for (const feature_id in date_wise_feature_updates) {
      const dates = Object.keys(date_wise_feature_updates[feature_id]);
      dates.forEach((date) => {
        state.report_data.push(...date_wise_feature_updates[feature_id][date]);
      });
    }

    state.import_summary.show = true;
    state.import_summary.total = state.report_data.length;
    state.report_data.forEach((row) => {
      if (row.__errors.length === 0) {
        state.import_summary.success += 1;
        row.__status = 'success';
      }
      else {
        state.import_summary.failed += 1;
        row.__status = 'failed';
      }
    });
  }
  catch (error) {
    state.progress_error_toast = $toast({
      title: $t('Error'),
      text: error.message,
      type: 'error',
      timeout: false,

      config: {
        closeOnClick: true,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
        draggable: false,
        showCloseButtonOnHover: false,
        hideProgressBar: true,
      },
    });
  }
}
</script>

<template>
  <hawk-modal-container :width="800" content_class="w-[800px]" @close="emits('close')">
    <Vueform v-model="form" sync size="sm" :display-errors="false" :endpoint="onSave">
      <div class="col-span-12">
        <hawk-modal-header @close="emits('close')">
          <template #title>
            {{ $t('Import progress') }}
          </template>
        </hawk-modal-header>
        <hawk-modal-content>
          <div v-if="!state.import_summary.show">
            <div class="text-sm font-semibold text-gray-700 mb-2">
              {{ $t('Get ready to import your data') }}
            </div>
            <div class="text-sm text-gray-500">
              {{ $t('Download the') }}
              <span class="text-primary-700 font-semibold cursor-pointer" @click="downloadTemplate">{{ $t('template') }},</span>
              {{ $t('update the progress values and reupload here') }}
            </div>
            <FileElement
              name="file"
              class="my-3"
              :presets="['hawk_file_element']"
              :options="{
                clickable_text: $t('Click to upload'),
                text: $t('or drag and drop'),
                description: $t('.csv files only'),
              }"
              accept=".csv"
              :auto="false"
              :drop="true"
              :use_uppy="false"
              @change="onImportFile"
            />
            <HawkAlertBanner v-if="form.file && state.progress_updates.length === 0" color="warning">
              <template #icon>
                <IconHawkAlertTriangle />
              </template>
              <template #content>
                {{ $t('No valid entries found') }}. {{ $t('Ensure that the Vector ID, Subactivity ID, Date, and Value columns are populated') }}.
              </template>
            </HawkAlertBanner>
          </div>
          <div v-else>
            <div class="mb-4">
              <div class="text-sm font-semibold text-gray-700">
                {{ $t('Summary') }}
              </div>
              <div class="text-xs text-gray-500">
                {{ $t('Upload successful') }}. {{ $t('Here is a summary of rows processed') }}
              </div>
            </div>
            <div class="grid grid-cols-3 gap-2 text-sm">
              <div class="border border-gray-200 p-3 rounded-lg text-gray-900">
                <div class="font-medium mb-2">
                  {{ $t('Total rows processed') }}
                </div>
                <div class="text-2xl font-bold">
                  {{ state.import_summary.total }}
                </div>
              </div>
              <div class="border border-gray-200 p-3 rounded-lg bg-error-25 text-gray-900">
                <div class="font-medium mb-2">
                  {{ $t('Failed rows') }}
                </div>
                <div class="text-2xl font-bold text-error-500">
                  {{ state.import_summary.failed }}
                </div>
              </div>
              <div class="border border-gray-200 p-3 rounded-lg bg-success-25 text-gray-900">
                <div class="font-medium mb-2">
                  {{ $t('Successful updates') }}
                </div>
                <div class="text-2xl font-bold text-success-500">
                  {{ state.import_summary.success }}
                </div>
              </div>
            </div>
            <HawkButton type="outlined" class="mt-4" @click="downloadReport()">
              <IconHawkDownloadOne class="w-4 h-4" />
              {{ $t('Download report') }}
            </HawkButton>
          </div>
        </hawk-modal-content>
        <hawk-modal-footer>
          <template #right>
            <div class="flex justify-end">
              <div v-if="state.import_summary.show">
                <HawkButton type="outlined" @click="emits('close')">
                  {{ $t('Done') }}
                </HawkButton>
              </div>
              <div v-else class="flex gap-2 items-center">
                <HawkButton type="outlined" @click="emits('close')">
                  {{ $t('Cancel') }}
                </HawkButton>
                <ButtonElement
                  button-class="bg-blue-600"
                  name="submit"
                  :disabled="!state.progress_updates.length"
                  :submits="true"
                >
                  {{ $t("Import") }}
                </ButtonElement>
              </div>
            </div>
          </template>
        </hawk-modal-footer>
      </div>
    </Vueform>
  </hawk-modal-container>
</template>

<style scoped lang="scss">

</style>
